import { FC, useEffect, useState } from 'react';
import {
  Loader,
  PhoneTextField,
  SelectAsync,
  StatesDropdown,
  TextField
} from '../../../../components';
import { useFormikContext } from 'formik';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Button,
  Box,
  FormHelperText,
  Autocomplete
} from '@mui/material';
import { useFlags } from '../../../../hooks';
import { AddExternalAgentModal } from './add-external-agent-modal';
import { getClientAgents } from '../../../../fetch';
import { getAgentsLookup } from '../../../../fetch/lookups';
import { IClientAgent, IClientDetail, IPeopleAgent } from '../../../../models';

interface IContactInfoTab {
  userId?: number;
  isModal?: boolean;
  showHasAgent?: boolean;
  clientDetail: IClientDetail;
  disableAgentField?: boolean;
}

export const ContactInfoTab: FC<IContactInfoTab> = ({
  userId,
  isModal = false,
  showHasAgent = true,
  clientDetail,
  disableAgentField
}) => {
  const { setFieldValue, setFieldTouched, values } = useFormikContext<any>();
  const featureFlags = useFlags();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [agents, setAgents] = useState<IClientAgent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<IClientAgent | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (
      agents.length > 0 &&
      values.contactInformation.agentFirstName &&
      values.contactInformation.agentLastName
    ) {
      const existingAgent = agents.find(
        agent =>
          agent.firstName.toLowerCase() ===
            values.contactInformation.agentFirstName.toLowerCase() &&
          agent.lastName.toLowerCase() === values.contactInformation.agentLastName.toLowerCase() &&
          agent.email.toLowerCase() === values.contactInformation.agentEmail.toLowerCase()
      );

      if (existingAgent && (!selectedAgent || selectedAgent.personId !== existingAgent.personId)) {
        setSelectedAgent(existingAgent);
      }
    }
  }, [
    agents,
    values.contactInformation.agentFirstName,
    values.contactInformation.agentLastName,
    values.contactInformation.agentEmail,
    setFieldValue,
    selectedAgent
  ]);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const data = await getClientAgents();
      setAgents(data);
    } catch (err) {
      setError('Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Fetch the updated list of agents after adding the new agent
      await fetchAgents();
    } catch (error) {
      setError('Error saving purchaser details');
    }
  };

  const handleImportAddressClick = () => {
    if (clientDetail && clientDetail.contactInformation) {
      const fields = [
        ['address', clientDetail.contactInformation.address],
        ['address2', clientDetail.contactInformation.address2],
        ['city', clientDetail.contactInformation.city],
        ['state', clientDetail.contactInformation.state],
        ['zip', clientDetail.contactInformation.zip]
      ];

      fields.forEach(([key, value]) => {
        setFieldValue(`contactInformation.${key}`, value, false); // true to validate immediately
        setFieldTouched(`contactInformation.${key}`, true, false);
      });
    }
  };

  const handleAgentChange = (newValue: IClientAgent | null) => {
    setIsAutocompleteOpen(false);
    setSelectedAgent(newValue);
    if (newValue) {
      // Update Formik values
      setFieldValue('contactInformation.externalAgentId', newValue.personId || null);
      setFieldValue('contactInformation.agentFirstName', newValue.firstName || '');
      setFieldValue('contactInformation.agentLastName', newValue.lastName || '');
      setFieldValue('contactInformation.agentEmail', newValue.email || '');
      setFieldValue('contactInformation.agentPhone', newValue.phone || '');
      setFieldValue('contactInformation.agentSellingCompany', newValue.company || '');
    } else {
      // Clear Formik values if no agent is selected
      setFieldValue('contactInformation.externalAgentId', null);
      setFieldValue('contactInformation.agentFirstName', '');
      setFieldValue('contactInformation.agentLastName', '');
      setFieldValue('contactInformation.agentEmail', '');
      setFieldValue('contactInformation.agentPhone', '');
      setFieldValue('contactInformation.agentSellingCompany', '');
    }
    setInputValue(''); // Reset input value
  };

  if (loading) return <Loader type="inline" position="centered" />;
  if (error) return <FormHelperText error>{error}</FormHelperText>;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={isModal ? 12 : 6}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <SelectAsync
              label="Agent"
              name="contactInformation.agentId"
              required
              hasClear={values?.contactInformation?.agentId && !userId}
              apiRequest={() => getAgentsLookup(true, values?.contactInformation?.agentId)}
              disabled={disableAgentField}
              transformResponse={(res: IPeopleAgent[]) =>
                res.map(r => ({
                  label: `${r.firstName} ${r.lastName}, ${r.workplace}`,
                  value: r.personId.toString(),
                  disabled: !r.isActive
                }))
              }
              disabledText="(Inactive)"
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="contactInformation.firstName"
                  required
                  autoComplete="nope"
                  inputProps={{
                    'data-lpignore': true
                  }}
                  label="First Name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="contactInformation.lastName"
                  required
                  autoComplete="nope"
                  inputProps={{
                    'data-lpignore': true
                  }}
                  label="Last Name"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="contactInformation.contractName"
                  required
                  autoComplete="nope"
                  inputProps={{
                    'data-lpignore': true
                  }}
                  label="Contract Name"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              name="contactInformation.email"
              autoComplete="nope"
              inputProps={{
                'data-lpignore': true
              }}
              label="Email"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PhoneTextField
              required
              name="contactInformation.phoneNumber"
              autoComplete="nope"
              inputProps={{
                'data-lpignore': true
              }}
              label="Phone Number"
              useDefaultOnChange
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} lg={isModal ? 12 : 6}>
        <Grid container spacing={1}>
          {isModal && (
            <Box ml={1}>
              <Button variant="contained" color="primary" onClick={handleImportAddressClick}>
                Import Purchaser Address
              </Button>
            </Box>
          )}
          <Grid item xs={12} sm={6} lg={12} xl={6}>
            <TextField
              required
              name="contactInformation.address"
              autoComplete="nope"
              inputProps={{
                'data-lpignore': true
              }}
              label="Address Line 1"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="contactInformation.address2"
              autoComplete="nope"
              inputProps={{
                'data-lpignore': true
              }}
              label="Address Line 2"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={6} xl={4}>
            <TextField
              required
              fullWidth
              name="contactInformation.city"
              autoComplete="nope"
              inputProps={{
                'data-lpignore': true
              }}
              label="City"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={6} xl={4}>
            <StatesDropdown
              isRequired
              value={values.contactInformation.state}
              handleChange={value => {
                setFieldValue('contactInformation.state', value as string);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={6} xl={4}>
            <TextField
              required
              fullWidth
              name="contactInformation.zip"
              autoComplete="nope"
              inputProps={{
                'data-lpignore': true
              }}
              label="Zip"
            />
          </Grid>
          {/* LEGACY ADD/HANDLE AGENT TO BE REMOVED WHEN LOOKUP IS FINALIZED */}
          {showHasAgent && !featureFlags.salesAgentLookup && (
            <Grid item xs={12}>
              <FormControlLabel
                color="primary"
                control={
                  <Checkbox
                    checked={values.contactInformation.buyerHasAgent}
                    name="contactInformation.buyerHasAgent"
                    id="buyerHasAgent"
                    onChange={(_, checked) => {
                      setFieldValue('contactInformation.buyerHasAgent', checked);
                      if (
                        values.contactInformation.agentFirstName ||
                        values.contactInformation.agentLastName ||
                        values.contactInformation.agentEmail ||
                        values.contactInformation.agentPhone ||
                        values.contactInformation.agentSellingCompany
                      ) {
                        setFieldValue('contactInformation.agentFirstName', ''); // Reset value to default
                        setFieldValue('contactInformation.agentLastName', ''); // Reset value to default
                        setFieldValue('contactInformation.agentEmail', ''); // Reset value to default
                        setFieldValue('contactInformation.agentPhone', ''); // Reset value to default
                        setFieldValue('contactInformation.agentSellingCompany', ''); // Reset value to default
                        if (checked) {
                          setFieldTouched('contactInformation.agentFirstName', false); // Reset touched to default
                          setFieldTouched('contactInformation.agentLastName', false); // Reset touched to default
                          setFieldTouched('contactInformation.agentEmail', false); // Reset touched to default
                          setFieldTouched('contactInformation.agentPhone', false); // Reset touched to default
                          setFieldTouched('contactInformation.agentSellingCompany', false); // Reset touched to default
                        }
                      }
                    }}
                  />
                }
                label="Buyer Has Agent"
              />
            </Grid>
          )}
          {values.contactInformation.buyerHasAgent && !featureFlags.salesAgentLookup && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  required={!!values.contactInformation.buyerHasAgent}
                  name="contactInformation.agentFirstName"
                  autoComplete="nope"
                  inputProps={{
                    'data-lpignore': true
                  }}
                  label="Selling Agent First Name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required={!!values.contactInformation.buyerHasAgent}
                  name="contactInformation.agentLastName"
                  inputProps={{
                    'data-lpignore': true
                  }}
                  autoComplete="nope"
                  label="Selling Agent Last Name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required={!!values.contactInformation.buyerHasAgent}
                  name="contactInformation.agentEmail"
                  autoComplete="nope"
                  inputProps={{
                    'data-lpignore': true
                  }}
                  label="Selling Agent Email"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PhoneTextField
                  required={!!values.contactInformation.buyerHasAgent}
                  name="contactInformation.agentPhone"
                  autoComplete="nope"
                  inputProps={{
                    'data-lpignore': true
                  }}
                  label="Selling Agent Phone Number"
                  useDefaultOnChange
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required={!!values.contactInformation.buyerHasAgent}
                  name="contactInformation.agentSellingCompany"
                  autoComplete="nope"
                  inputProps={{
                    'data-lpignore': true
                  }}
                  label="Selling Company"
                />
              </Grid>
            </>
          )}
          {/* END LEGACY CODE HERE */}
          {/* START NEW LOOKUP HERE - REMOVE FEATURE FLAG CONDITION TO VIEW AND USE */}
          {showHasAgent && featureFlags.salesAgentLookup && (
            <Grid item xs={12}>
              <FormControlLabel
                color="primary"
                control={
                  <Checkbox
                    checked={values.contactInformation.buyerHasAgent}
                    name="contactInformation.buyerHasAgent"
                    id="buyerHasAgent"
                    onChange={(_, checked) => {
                      setFieldValue('contactInformation.buyerHasAgent', checked);
                      if (
                        values.contactInformation.agentFirstName ||
                        values.contactInformation.agentLastName ||
                        values.contactInformation.agentEmail ||
                        values.contactInformation.agentPhone ||
                        values.contactInformation.agentSellingCompany
                      ) {
                        setFieldTouched('contactInformation.agentFirstName', false);
                        setFieldTouched('contactInformation.agentLastName', false);
                        setFieldTouched('contactInformation.agentEmail', false);
                        setFieldTouched('contactInformation.agentPhone', false);
                        setFieldTouched('contactInformation.agentSellingCompany', false);
                      }
                    }}
                  />
                }
                label="Buyer Has Agent"
              />
              {values.contactInformation.buyerHasAgent && featureFlags.salesAgentLookup && (
                <>
                  <Autocomplete
                    sx={{ mt: 0.8, mb: 0.8 }}
                    options={
                      inputValue.length >= 2 || isAutocompleteOpen
                        ? Array.from(
                            new Map(
                              agents
                                .filter(agent => {
                                  const fullName =
                                    `${agent.firstName} ${agent.lastName}`.toLowerCase();
                                  const reverseName =
                                    `${agent.lastName} ${agent.firstName}`.toLowerCase();
                                  const inputLower = inputValue.toLowerCase();
                                  return (
                                    fullName.startsWith(inputLower) ||
                                    reverseName.startsWith(inputLower) ||
                                    agent.firstName.toLowerCase().startsWith(inputLower) ||
                                    agent.lastName.toLowerCase().startsWith(inputLower)
                                  );
                                })
                                .map(agent => [`${agent.firstName} ${agent.lastName}`, agent])
                            ).values()
                          )
                        : agents
                    }
                    getOptionLabel={option => `${option.firstName} ${option.lastName}`}
                    value={selectedAgent || null} // Ensure value matches an option
                    onChange={(event, newValue) => {
                      handleAgentChange(newValue as IClientAgent | null);
                    }}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue) => {
                      setInputValue(newInputValue);
                      if (newInputValue === '') {
                        setIsAutocompleteOpen(true); // Open the dropdown when input is cleared
                      }
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        name="contactInformation.externalAgentId"
                        autoComplete="none"
                        label="Search for Agent"
                        variant="outlined"
                        fullWidth
                        required
                        onFocus={() => setIsAutocompleteOpen(true)}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.personId === value?.personId}
                    filterOptions={(options, state) =>
                      state.inputValue
                        ? options.filter(
                            option =>
                              `${option.firstName} ${option.lastName}`
                                .toLowerCase()
                                .includes(state.inputValue.toLowerCase()) ||
                              `${option.lastName} ${option.firstName}`
                                .toLowerCase()
                                .includes(state.inputValue.toLowerCase())
                          )
                        : options
                    }
                    noOptionsText={inputValue.length >= 2 ? 'No options' : ''}
                    loading={loading}
                    onBlur={() => setIsAutocompleteOpen(false)}
                    onClose={() => setIsAutocompleteOpen(false)}
                    open={isAutocompleteOpen}
                  />
                  <Grid item xs={12} sm={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth={true} // Ensures full width on mobile
                      sx={{
                        width: { xs: '100%', sm: 'auto' } // Full width on mobile, auto on desktop
                      }}
                      onClick={() => setIsModalOpen(true)}
                    >
                      Add New Agent
                    </Button>
                    <AddExternalAgentModal
                      open={isModalOpen}
                      onClose={() => setIsModalOpen(false)}
                      handleSave={handleSave}
                      agentId={values?.contactInformation?.agentId}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
          {values.contactInformation.buyerHasAgent && featureFlags.salesAgentLookup && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  required={!!values.contactInformation.buyerHasAgent}
                  name="contactInformation.agentFirstName"
                  autoComplete="nope"
                  label="Selling Agent First Name"
                  value={values.contactInformation.agentFirstName || ''}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required={!!values.contactInformation.buyerHasAgent}
                  name="contactInformation.agentLastName"
                  autoComplete="nope"
                  label="Selling Agent Last Name"
                  value={values.contactInformation.agentLastName || ''}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required={!!values.contactInformation.buyerHasAgent}
                  name="contactInformation.agentEmail"
                  autoComplete="nope"
                  label="Selling Agent Email"
                  value={values.contactInformation.agentEmail || ''}
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PhoneTextField
                  name="contactInformation.agentPhone"
                  autoComplete="nope"
                  label="Selling Agent Phone Number"
                  value={values.contactInformation.agentPhone || ''}
                  useDefaultOnChange
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="contactInformation.agentSellingCompany"
                  autoComplete="nope"
                  label="Selling Company"
                  value={values.contactInformation.agentSellingCompany || ''}
                  disabled={true}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
